import React, { ReactNode, useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export interface PromoBannerProps {
    title: string;
    description: string;
    actionLabel: string;
    onAction: () => void;
    actionIcon?: ReactNode;
    secondaryLabel?: string;
    onSecondary?: () => void;
    /** When set, dismissing writes this key to localStorage and the banner stays gone. */
    dismissKey?: string;
    illustration?: string;
}

/**
 * A house ad, and the means to be rid of it.
 *
 * Dismissal is persisted: a permanent banner above the vendor's own numbers
 * on every visit is rent we have not earned.
 */
export const PromoBanner: React.FC<PromoBannerProps> = ({
    title,
    description,
    actionLabel,
    onAction,
    actionIcon,
    secondaryLabel,
    onSecondary,
    dismissKey,
    illustration,
}) => {
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (dismissKey && localStorage.getItem(dismissKey) === '1') setDismissed(true);
    }, [dismissKey]);

    if (dismissed) return null;

    const dismiss = () => {
        if (dismissKey) localStorage.setItem(dismissKey, '1');
        setDismissed(true);
    };

    return (
        <Card className="relative gap-0 overflow-hidden py-0">
            <div className="flex items-center justify-between gap-6 p-5">
                <div className="min-w-0 max-w-xl">
                    <h3 className="font-heading text-base font-medium tracking-tight">{title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{description}</p>

                    <div className="mt-4 flex items-center gap-2">
                        <Button size="sm" onClick={onAction} className="gap-1.5">
                            {actionIcon}
                            {actionLabel}
                        </Button>
                        {secondaryLabel && onSecondary && (
                            <Button size="sm" variant="ghost" onClick={onSecondary}>
                                {secondaryLabel}
                            </Button>
                        )}
                    </div>
                </div>

                {illustration && (
                    <img src={illustration} alt="" className="hidden h-32 w-auto object-contain lg:block" />
                )}
            </div>

            {dismissKey && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="absolute end-2 top-2 size-7 text-muted-foreground"
                >
                    <X className="size-4" />
                </Button>
            )}
        </Card>
    );
};
